/**
 * Pure helpers for report file naming and per-fetus splitting.
 * Deliberately free of React / PDF imports so they can be unit-tested directly.
 */

// Exam types whose PDF has one page (or more) per fetus, each labelled
// "FETUS A", "FETUS B"... on the page itself.
const PER_FETUS_EXAM_TYPES = ['Second Trimester OB', 'BPP'];

export function fetusLetter(index) {
  return String.fromCharCode(65 + index);
}

/**
 * Current PDF filename convention, without extension:
 * First_Last_MRN_ExamType
 */
export function buildReportBaseName(formData, examType) {
  return `${formData.firstName}_${formData.lastName}_${formData.mrn}_${examType}`;
}

/**
 * Split a report into independently-rasterised sections.
 * Multi-fetus reports become one section per fetus (label "fetus_A",
 * "fetus_B"...), so each fetus's pages can be named for that fetus.
 * Every other report is a single section with no label.
 *
 * `onlyFetusIndex` tells the PDF generator to render just that fetus while
 * still knowing the study is multi-fetus (so titles keep their "Fetus A" wording).
 */
export function getReportSections(examType, formData) {
  const fetuses = Array.isArray(formData.fetuses) ? formData.fetuses : [];
  if (!PER_FETUS_EXAM_TYPES.includes(examType) || fetuses.length < 2) {
    return [{ label: null, data: formData }];
  }
  return fetuses.map((_, i) => ({
    label: `fetus_${fetusLetter(i)}`,
    data: { ...formData, onlyFetusIndex: i }
  }));
}

/**
 * PNG filename for one page of one section.
 *   single page, no fetus:   base.png
 *   multi page, no fetus:    base_p1.png, base_p2.png
 *   fetus, single page:      base_fetus_A.png
 *   fetus, multi page:       base_fetus_A_p1.png, base_fetus_A_p2.png
 */
export function buildPngFileName(baseName, sectionLabel, pageIndex, pageCount) {
  const parts = [baseName];
  if (sectionLabel) parts.push(sectionLabel);
  if (pageCount > 1) parts.push(`p${pageIndex + 1}`);
  return `${parts.join('_')}.png`;
}
